import { removeDiacritics } from '@oskecare/fe-utilities/scripts/helpers.js';
import { Basket } from './models/Basket.js';
import { Service, CATALOG_TYPE_PRIMA_KIDS } from './models/Service.js';
import { Price } from './models/Price.js';
import { Category } from './models/Category.js';
import { Tariff } from './models/Tariff.js';
import {
  TryAndBuy,
  TRY_AND_BUY_STATUS_INIT,
  TRY_AND_BUY_PERIOD_UNIT_DAYS,
  TRY_AND_BUY_STATUS_ATTRIBUTE_ID,
  TRY_AND_BUY_END_DATE_ATTRIBUTE_ID,
} from './models/TryAndBuy.js';
import {
  AmountAttribute,
  AMOUNT_ATTRIBUTE_ID,
  AMOUNT_ATTRIBUTE_TYPE,
} from './models/attributes/AmountAttribute.js';
import {
  LicenceCountAttribute,
  LICENCE_COUNT_ATTRIBUTE_ID,
} from './models/attributes/LicenceCountAttribute.js';
import {
  SnListAttribute,
  SN_LIST_ATTRIBUTE_ID,
  SN_LIST_ATTRIBUTE_TYPE,
  LIST_LENGTH_ATTRIBUTE_PROPERTY_ID,
} from './models/attributes/SnListAttribute.js';
import {
  NEW_SN_LIST_ATTRIBUTE_ID,
  NEW_SN_LIST_ATTRIBUTE_TYPE,
  NewSnListAttribute,
} from './models/attributes/NewSnListAttribute.js';
import {
  GoogleAccountAttribute,
  GOOGLE_ACCOUNT_ATTRIBUTE_ID,
  GOOGLE_ACCOUNT_ATTRIBUTE_TYPE,
} from './models/attributes/GoogleAccountAttribute.js';
import {
  ContactSnAttribute,
  CONTACT_SN_ATTRIBUTE_ID,
  CONTACT_SN_ATTRIBUTE_TYPE,
} from './models/attributes/ContactSnAttribute.js';
import {
  AmountAttributeOption,
  AMOUNT_ATTRIBUTE_OPTION_ID,
} from './models/attributes/AmountAttributeOption.js';
import {
  EInvoiceNotificationTimeAttribute,
  DAY_OF_WEEK_ATTRIBUTE_ID,
  TIME_OF_DAY_ATTRIBUTE_ID,
  OPTION_LIMITED,
  OPTION_NONSTOP,
  EINVOICE_NOTIFICATION_TIME_ATTRIBUTE_TYPE,
} from './models/attributes/EInvoiceNotificationTimeAttribute.js';
import {
  EInvoiceReportEmailAttribute,
  EINVOICE_REPORT_EMAIL_ATTRIBUTE_TYPE,
  REPORT_EMAIL_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceReportEmailAttribute.js';
import {
  EInvoiceEncryptReportAttribute,
  EINVOICE_ENCRYPT_REPORT_ATTRIBUTE_TYPE,
  ENCRYPTION_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceEncryptReportAttribute.js';
import {
  EInvoiceNotificationSnAttribute,
  EINVOICE_NOTIFICATION_SN_ATTRIBUTE_TYPE,
  NOTIFICATION_SN_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotificationSnAttribute.js';
import {
  EInvoiceNotificationEmailAttribute,
  EINVOICE_NOTIFICATION_EMAIL_ATTRIBUTE_TYPE,
  NOTIFICATION_EMAIL_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotificationEmailAttribute.js';
import {
  EInvoiceNotifySlavesAttribute,
  EINVOICE_NOTIFY_SLAVES_ATTRIBUTE_TYPE,
  NOTIFY_SLAVES_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotifySlavesAttribute.js';
import {
  EInvoiceNotifyMasterAttribute,
  EINVOICE_NOTIFY_MASTER_ATTRIBUTE_TYPE,
  NOTIFY_MASTER_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceNotifyMasterAttribute.js';
import {
  EInvoiceNotifyWithPrice,
  EINVOICE_NOTIFY_WITH_PRICE_TYPE,
} from './models/attributes/EInvoiceNotifyWithPrice.js';
import {
  AddressAttribute,
  STREET_NAME_ATTRIBUTE_ID,
  CITY_ATTRIBUTE_ID,
  HOUSE_NUMBER_ATTRIBUTE_ID,
  ZIP_CODE_ATTRIBUTE_ID,
} from './models/attributes/AddressAttribute.js';
import {
  DeviceAttribute,
  DEVICE_NAME_ATTRIBUTE_ID,
  IMEI_ATTRIBUTE_ID,
} from './models/attributes/DeviceAttribute.js';
import {
  EInvoicePasswordAttribute,
  EINVOICE_PASSWORD_ATTRIBUTE_TYPE,
  PASSWORD_ATTRIBUTE_ID,
} from './models/attributes/EInvoicePasswordAttribute.js';
import {
  EInvoicePasswordConfirmAttribute,
  PASSWORD_CONFIRM_ATTRIBUTE_ID,
} from './models/attributes/EInvoiceConfirmPasswordAttribute.js';
import {
  EmailAttribute,
  EMAIL_ATTRIBUTE_ID,
  EMAIL_ATTRIBUTE_TYPE,
} from './models/attributes/EmailAttribute.js';
import {
  EndDateListAttribute,
  END_DATE_LIST_ATTRIBUTE_ID,
} from './models/attributes/EndDateListAttribute.js';
import { Summary } from './models/Summary.js';
import { SummaryItem } from './models/SummaryItem.js';
import {
  TogetherFreeGroupCountAttribute,
  TOGETHER_FREE_GROUP_COUNT_ATTRIBUTE_ID,
} from './models/attributes/TogetherFreeGroupCount.js';
import {
  DataCapAttribute,
  DATA_CAP_ATTRIBUTE_ID,
  DATA_CAP_ATTRIBUTE_TYPE,
} from './models/attributes/DataCapAttribute.js';
import {
  SnAttribute,
  SN_ATTRIBUTE_ID,
  SN_ATTRIBUTE_TYPE,
} from './models/attributes/SnAttribute.js';
import {
  ConsumedAmountAttribute,
  CONSUMED_AMOUNT_ATTRIBUTE_ID,
  CONSUMED_AMOUNT_ATTRIBUTE_TYPE,
} from './models/attributes/ConsumedAmount.js';
import {
  CyberFilterContactSnAttribute,
  CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID,
  CYBER_FILTER_CONTACT_SN_ATTRIBUTE_TYPE,
} from './models/attributes/CyberFilterContactSn.js';
import {
  PrimaKidsPrepaidSnList,
  PRIMA_KIDS_PREPAID_SN_LIST_ATTRIBUTE_ID,
  PRIMA_KIDS_PREPAID_SN_LIST_ATTRIBUTE_TYPE,
  PRIMA_KIDS_PREPAID_SN_LIST_WAITING_ATTRIBUTE_ID,
} from './models/attributes/PrimaKidsPrepaidSnListAttribute.js';
import {
  PrimaKidsRemainingMonthlyLimit,
  PRIMA_KIDS_REMAINING_MONTHLY_LIMIT_ATTRIBUTE_ID,
  PRIMA_KIDS_REMAINING_MONTHLY_LIMIT_ATTRIBUTE_TYPE,
} from './models/attributes/PrimaKidsRemainingMonthlyLimitAttribute.js';
import {
  VolumeAttribute,
  VOLUME_ATTRIBUTE_ID,
  VOLUME_ATTRIBUTE_TYPE,
} from './models/attributes/VolumeAttribute.js';
import { ChannelsAttribute } from './models/attributes/ChannelsAttribute.js';
import {
  CyberFilterLicenseListAttribute,
  CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID,
} from './models/attributes/CyberFilterLicenseList.js';
import { Period } from './models/Period.js';
import { RELATED_SERVICE_TYPES } from './constants.js';
import PostProcessor from './PostProcessor.js';

const CHARACTERISTIC_VALUE_OPTIONAL_NAME = 'Optional Name';
const CHARACTERISTIC_VALUE_RELATED_SERVICE = 'Related Service';
const CHARACTERISTIC_VALUE_RELATED_SERVICE_TYPE = 'Related Service Type';

/**
 * A class that contains all of the logic related to parsing data from OST responses.
 * Raw responses are not to be read anywhere else in the module, all further processing
 * should be done using the models created in this class. If any of the data is missing,
 * you should add it to the models and add the parsing logic to this class.
 */
export default class Parser {
  /**
   * Main method for processing OST responses.
   * Accepts catalog and init responses, optionally also update response.
   * Returns processed all of the processed data in the Basket model.
   *
   * @param {Object} catalogResponse
   * @param {Object} initResponse
   * @param {?Object} updateResponse
   * @param {?Object} summaryResponse
   * @param {boolean} readOnly
   * @param {string} subscriptionId
   * @return {Basket}
   * @static
   */
  static createBasket({
    catalogResponse,
    initResponse,
    updateResponse,
    summaryResponse,
    readOnly = false,
    subscriptionId,
  }) {
    if (!initResponse || !catalogResponse) {
      throw new Error('`createBasket` requires init and catalog responses.');
    }

    const basket = new Basket({
      subscriptionId,
      basketId: initResponse.onlineBasketId,
      tariff: Parser._parseTariff(initResponse),
      initialBasketPrice: Parser._extractBasketPrices(initResponse),
      updatedBasketPrice: Parser._extractBasketPrices(updateResponse),
      minimalMonthlyFee: Parser._extractMinimalMonthlyFee(initResponse),
      currentMonthlyFee: Parser._extractCurrentMonthlyFee(initResponse),
      monthlyFeeDiff: Parser._extractMonthlyFeeDiff(initResponse),
      isReadOnly: readOnly,
      isSuspended: Parser._parseIsSuspended(initResponse),
      hasInvoiceOverdue: Parser._parseHasInvoiceOverdue(initResponse),
      isChangingTariff: Parser._parseIsChangingTariff(initResponse),
    });

    const rawCatalogItems = Parser._extractRawCatalogItems(catalogResponse);
    const rawCatalogPrimaKidsItems = Parser._extractRawCatalogItems(
      catalogResponse,
      'ecarePrimaKidsServices',
    );

    if (rawCatalogItems.size === 0) {
      throw new Error(`getCatalog response is malformed or does not contain any items`);
    }

    const initData = {
      rawOrderItems: Parser._extractRawOrderItems(initResponse),
      rawEligibleItems: Parser._extractRawEligibleItems(initResponse),
      rawInProgressItems: Parser._extractRawInProgressItems(initResponse),
      rawOldAttributeValuesItems: Parser._extractRawOldAttributeValuesItems(initResponse),
      rawUnprocessedRequestItems: Parser._extractRawUnprocessedRequestItems(initResponse),
    };

    if (!updateResponse || !summaryResponse) {
      Parser._addBaseServiceDataToBasket({ basket, rawCatalogItems, ...initData });
      Parser._addBaseServiceDataToBasket({
        basket,
        rawCatalogItems: rawCatalogPrimaKidsItems,
        catalogType: CATALOG_TYPE_PRIMA_KIDS,
        ...initData,
      });
      PostProcessor.postProcessBasket(basket);
      return basket;
    }

    // replace by fees from updated response
    basket.minimalMonthlyFee = Parser._extractMinimalMonthlyFee(updateResponse);
    basket.currentMonthlyFee = Parser._extractCurrentMonthlyFee(updateResponse);
    basket.monthlyFeeDiff = Parser._extractMonthlyFeeDiff(updateResponse);

    const updateData = {
      rawOrderItems: Parser._extractRawOrderItems(updateResponse, true),
      rawEligibleItems: Parser._extractRawEligibleItems(updateResponse, true),
      rawInProgressPrimaKidsItems: Parser._extractRawInProgressItems(
        updateResponse,
        'inProgressPrimaKidsServicesInfo',
      ),
    };

    const { addedSummaryItems, changedSummaryItems, removedSummaryItems } =
      Parser._extractRawSummaryItems(summaryResponse);

    Parser._addBaseServiceDataToBasket({
      basket,
      rawCatalogItems,
      ...updateData,
      rawInProgressItems: initData.rawInProgressItems,
      rawOldAttributeValuesItems: initData.rawOldAttributeValuesItems,
      rawUnprocessedRequestItems: initData.rawUnprocessedRequestItems,
    });
    Parser._addBaseServiceDataToBasket({
      basket,
      rawCatalogItems: rawCatalogPrimaKidsItems,
      catalogType: CATALOG_TYPE_PRIMA_KIDS,
      ...updateData,
      rawInProgressItems: updateData.rawInProgressPrimaKidsItems,
      rawOldAttributeValuesItems: initData.rawOldAttributeValuesItems,
      rawUnprocessedRequestItems: initData.rawUnprocessedRequestItems,
    });

    const { previouslyAddedServiceIds, previouslyRemovedServiceIds } =
      Parser._extractPreviouslyUpdated(updateResponse);

    Parser._addUpdateSpecificServiceDataToBasket(
      basket,
      previouslyAddedServiceIds,
      previouslyRemovedServiceIds,
      initData.rawOrderItems,
      initData.rawInProgressItems,
      initData.rawOldAttributeValuesItems,
      addedSummaryItems,
      changedSummaryItems,
      removedSummaryItems,
    );

    PostProcessor.postProcessBasket(basket);

    return basket;
  }

  /**
   * Main data processing method, contains parsing logic common for both init and update responses.
   *
   * Each service will be marked with `catalogType` if provided.
   * This is used for differentiation of various catalog variants like Prima Kids subservices.
   *
   * @param {Basket} basket
   * @param {Map<number, object>} rawCatalogItems
   * @param {Map<number, object>} rawEligibleItems
   * @param {Map<number, object>} rawOrderItems
   * @param {Map<number, object>} rawInProgressItems
   * @param {Map<number, object>} rawOldAttributeValuesItems
   * @param {Map<number, object>} rawUnprocessedRequestItems
   * @param {?string} catalogType - identificator of special service catalog type
   * @return {Basket}
   * @private
   * @static
   */
  static _addBaseServiceDataToBasket({
    basket,
    rawCatalogItems,
    rawEligibleItems,
    rawOrderItems,
    rawInProgressItems,
    rawOldAttributeValuesItems,
    rawUnprocessedRequestItems,
    catalogType = null,
  }) {
    const b = basket;
    rawCatalogItems.forEach((catalogItem, id) => {
      const eligibleItem = rawEligibleItems.get(id);
      const orderItem = rawOrderItems.get(id);
      const inProgressItem = rawInProgressItems.get(id);
      const oldAttributeValuesItem = rawOldAttributeValuesItems.get(id);
      const rawUnprocessedRequestItem = rawUnprocessedRequestItems.get(catalogItem.uuid);

      // If catalog item isn't present in initResponse it's skipped
      if (!eligibleItem && !orderItem && !inProgressItem && !oldAttributeValuesItem) {
        return;
      }

      const service = new Service({
        id,
        uuid: Parser._parseUuid(catalogItem),
        name: Parser._parseName(catalogItem, orderItem, eligibleItem, inProgressItem),
        slug: Parser._parseSlug(catalogItem),
        price: Parser._parsePrice(catalogItem, orderItem, eligibleItem, inProgressItem),
        originalPrice: Parser._parseOriginalPrice(orderItem, eligibleItem),
        unitPrice: Parser._parseUnitPrice(orderItem, eligibleItem),
        period: Parser._parsePeriod(catalogItem),
        isPriceHidden: Parser._parseIsPriceHidden(catalogItem),
        description: Parser._parseDescription(catalogItem),
        category: Parser._parseCategory(catalogItem),
        mHubCategoryId: Parser._parseMHubCategoryId(catalogItem),
        isSubsidy: Parser._parseIsSubsidy(catalogItem),
        isRecommended: Parser._parseIsRecommended(catalogItem),
        isEligible: Parser._parseIsEligible(eligibleItem, inProgressItem),
        isActive: Parser._parseIsActive(orderItem, inProgressItem),
        canBeDeactivated: Parser._parseCanBeDeactivated(catalogItem),
        isActivating: Parser._parseIsActivating(inProgressItem),
        isDeactivating: Parser._parseIsDeactivating(inProgressItem),
        isInitiallyActive: Parser._parseIsActive(orderItem, inProgressItem),
        isInitiallyChanging: Parser._parseIsInitiallyChanging(
          orderItem,
          oldAttributeValuesItem,
          inProgressItem,
        ),
        customerOrderId: Parser._parseCustomerOrderId(orderItem),
        activationDate: Parser._parseActivationDate(orderItem),
        changeDate: Parser._parseChangeDate(inProgressItem),
        renewalDate: Parser._parseRenewalDate(orderItem),
        order: Parser._parseOrder(catalogItem),
        isIdRcVerificationRequired: Parser._parseIsIdRcVerificationRequired(catalogItem),
        hasUnprocessedRequest: Parser._parseHasUnprocessedRequest(
          orderItem,
          eligibleItem,
          rawUnprocessedRequestItem,
          inProgressItem,
        ),
        attributes: Parser._parseAttributes(
          catalogItem,
          orderItem,
          eligibleItem,
          oldAttributeValuesItem,
        ),
        catalogType,
        tryAndBuy: Parser._parseTryAndBuy(catalogItem, orderItem, eligibleItem),
        relatedServiceUuid: Parser._parseRelatedServiceUuid(orderItem, eligibleItem),
        relatedServiceType: Parser._parseRelatedServiceType(orderItem, eligibleItem),
        canBeModifiedDuringTariffChange: Parser._parseCanServiceBeModifiedDuringChange(catalogItem),
      });

      b.services.push(service);
    });
    b.services.sort((s1, s2) => s1.order - s2.order);
    b.categoriesMap = new Map([
      ...b.categoriesMap,
      ...Parser._extractCategories(rawCatalogItems, catalogType),
    ]);

    return b;
  }

  /**
   * Secondary method for processing update specific data.
   *
   * isAdded/RemovedInLastUpdate are difference against previous (n-1) state.
   * added/removed/changedSummaryItems are difference against initial state.
   * These two cannot be combined or derived from each other.
   * Some operations can be contained in lastUpdate but not contained in summary data.
   *
   * Example:
   * Service, that was added during update flow (subsidy screen)
   * and was not initially activated can be deactivated and thus is contained in lastUpdate
   * and not in summary.
   *
   *
   * @see _initializeBasketWithData
   * @param {Basket} basket
   * @param {number[]} previouslyAddedServiceIds
   * @param {number[]} previouslyRemovedServiceIds
   * @param {Object[]} initRawOrderItems
   * @param {Object[]} initRawInProgressItems
   * @param {Object[]} initRawOldAttributeValuesItems
   * @param {Object[]} addedSummaryItems
   * @param {Object[]} changedSummaryItems
   * @param {Object[]} removedSummaryItems
   * @private
   */
  static _addUpdateSpecificServiceDataToBasket(
    basket,
    previouslyAddedServiceIds,
    previouslyRemovedServiceIds,
    initRawOrderItems,
    initRawInProgressItems,
    initRawOldAttributeValuesItems,
    addedSummaryItems,
    changedSummaryItems,
    removedSummaryItems,
  ) {
    basket.services.forEach(service => {
      const s = service;
      s.isAddedInLastUpdate = previouslyAddedServiceIds.some(id => id === s.id);
      s.isRemovedInLastUpdate = previouslyRemovedServiceIds.some(id => id === s.id);
      s.isInitiallyActive = Parser._parseIsActive(
        initRawOrderItems.get(s.id),
        initRawInProgressItems.get(s.id),
      );
      s.isInitiallyChanging = Parser._parseIsInitiallyChanging(
        initRawOrderItems.get(s.id),
        initRawOldAttributeValuesItems.get(s.id),
        initRawInProgressItems.get(s.id),
      );
      if (addedSummaryItems.has(s.id)) {
        s.isAdded = true;
        s.price = Parser._parseSummaryPrice(addedSummaryItems.get(s.id));
      } else if (removedSummaryItems.has(s.id)) {
        s.isRemoved = true;
        s.price = Parser._parseSummaryPrice(removedSummaryItems.get(s.id));
      } else if (changedSummaryItems.has(s.id)) {
        s.isUpdated = true;
        s.price = Parser._parseSummaryPrice(changedSummaryItems.get(s.id));
      }
    });
  }

  /**
   * Helps to pull catalog item data from catalog response.
   *
   * @param {Object} catalogResponse - Raw response from OST getCatalog endpoint
   * @param {string} sitepartKey - sitepart key from catalog response that will be extracted
   * @return {Map<number, Object>}
   * @private
   */
  static _extractRawCatalogItems(catalogResponse, sitepartKey = 'ecareServices') {
    const catalogItems = catalogResponse?.siteparts?.[sitepartKey]?.items;
    if (!catalogItems) {
      return new Map();
    }

    return Parser._getRawItems(catalogItems, item => parseInt(item.id, 10)); // TODO remove parseInt after OST string/number unification
  }

  /**
   * Helps to pull eligible item data from init of update response.
   *
   * @param {Object} initOrUpdateResponse
   * @param {boolean} isUpdate
   * @return {Map<number, Object>}
   * @private
   */
  static _extractRawEligibleItems(initOrUpdateResponse, isUpdate = false) {
    const items = initOrUpdateResponse?.eligibleOffer;
    if (!items) {
      throw new Error(`${isUpdate ? 'update' : 'init'} response is malformed`);
    }
    if (items.length === 0) {
      throw new Error(`${isUpdate ? 'update' : 'init'} response is missing eligible items`);
    }

    return Parser._getRawItems(
      items,
      item => parseInt(item.customerOrderId[0].id, 10), // TODO remove parseInt after OST string/number unification
    );
  }

  /**
   * Helps to pull order item data from init of update response.
   *
   * @param {Object} initOrUpdateResponse
   * @param {boolean} isUpdate
   * @return {Map<number, Object>}
   * @private
   */
  static _extractRawOrderItems(initOrUpdateResponse, isUpdate = false) {
    const items = initOrUpdateResponse?.ordr?.orderItem[0]?.childOrderItem;
    if (!items) {
      throw new Error(`${isUpdate ? 'update' : 'init'} response is malformed`);
    }
    if (isUpdate && items.length === 0) {
      throw new Error('update response is missing order items');
    }

    return Parser._getRawItems(
      items,
      item => parseInt(item.offerSpecification.id, 10), // TODO remove parseInt after OST string/number unification
    );
  }

  /**
   * Helps to pull inProgress item data from init or update response.
   *
   * @param {Object} initOrUpdateResponse
   * @param {string} key
   * @return {Map<number, Object>}
   * @private
   */
  static _extractRawInProgressItems(initOrUpdateResponse, key = 'inProgressServicesInfo') {
    if (!initOrUpdateResponse?.[key]) {
      return new Map();
    }

    return Parser._getRawItems(initOrUpdateResponse[key], item => parseInt(item.oid, 10)); // TODO remove parseInt after OST string/number unification
  }

  /**
   * oldAttributeValues contains similar structure as RawOrderItems.
   *
   * oldAttributeValues items exists only in service deactivation on billDay date case, because:
   * - service in this state is still active service until billDay date but listed as eligible service (inactive) and
   *   thus looses its attribute values (eligible has only default values)
   * - OST did not want to modify eligibleItem structure, so a new structure was added (oldAttributeValues)
   *
   * @param {Object} initOrUpdateResponse
   * @returns {Map<number, Object>}
   * @see _parseAttributeValue
   * @private
   */
  static _extractRawOldAttributeValuesItems(initOrUpdateResponse) {
    const items = initOrUpdateResponse?.oldAttributeValues;
    if (!items) {
      throw new Error('init/update response is malformed');
    }

    return Parser._getRawItems(
      items,
      item => parseInt(item.offerSpecification.oid, 10), // TODO remove parseInt after OST string/number unification
    );
  }

  /**
   * Helps to pull unprocessed service info data from init or update response.
   *
   * @param {Object} initOrUpdateResponse
   * @returns {Map<number, Object>}
   * @private
   */
  static _extractRawUnprocessedRequestItems(initOrUpdateResponse) {
    if (!initOrUpdateResponse?.unprocessedServicesInfo) {
      throw new Error('init/update response is malformed');
    }

    return Parser._getRawItems(
      initOrUpdateResponse.unprocessedServicesInfo,
      item => parseInt(item.uuid, 10), // TODO remove parseInt after OST string/number unification
    );
  }

  /**
   * Helps to pull previously updated item data from update response.
   *
   * @param {Object} updateResponse
   * @return {{previouslyAddedServiceIds: number[], previouslyRemovedServiceIds: number[]}}
   * @private
   */
  static _extractPreviouslyUpdated(updateResponse) {
    return {
      previouslyAddedServiceIds:
        updateResponse.differenceResultData?.addedCustomerOrderItem?.map(item =>
          parseInt(item.offerSpecification.id, 10),
        ) || [],
      previouslyRemovedServiceIds:
        updateResponse.differenceResultData?.removedCustomerOrderItem?.map(item =>
          parseInt(item.offerSpecification.id, 10),
        ) || [],
    };
  }

  /**
   * Helps to pull summary data from the summary response.
   *
   * @param {Object} summaryResponse
   * @return {{changedSummaryItems: Map<number, Object>, addedSummaryItems: Map<number, Object>, removedSummaryItems: Map<number, Object>}}
   * @private
   */
  static _extractRawSummaryItems(summaryResponse) {
    if (
      !summaryResponse?.addedCustomerOrderItem ||
      !summaryResponse?.changedCustomerOrderItem ||
      !summaryResponse?.removedCustomerOrderItem
    ) {
      throw new Error('summary response is malformed');
    }

    return {
      addedSummaryItems: Parser._getRawItems(
        summaryResponse.addedCustomerOrderItem,
        item => parseInt(item.id, 10), // TODO remove parseInt after OST string/number unification
      ),
      changedSummaryItems: Parser._getRawItems(
        summaryResponse.changedCustomerOrderItem,
        item => parseInt(item.id, 10), // TODO remove parseInt after OST string/number unification
      ),
      removedSummaryItems: Parser._getRawItems(
        summaryResponse.removedCustomerOrderItem,
        item => parseInt(item.id, 10), // TODO remove parseInt after OST string/number unification
      ),
    };
  }

  /**
   * Helper function to aid in extraction of indexed lists of items from the responses.
   *
   * @param {Object[]} list - Part of the response that contains list of service/product items
   * @param {Object => number} idSelector - Function that finds the OID in the item to be used for indexing
   * @return {Map<number, Object>}
   * @private
   */
  static _getRawItems(list, idSelector) {
    const map = new Map();
    list.forEach(item => {
      map.set(idSelector(item), item);
    });
    return map;
  }

  /**
   * Extracts and parses category data from catalog items.
   * NOTE: This iterates over all service items looking for new categories.
   * OST really needs to separate out category data from the product items to reduce duplicity.
   * This really should happen before category descriptions are added!
   * TODO: rewrite once OST addresses the issue
   *
   * @param {Object[]} rawCatalogItems
   * @param {?string} catalogType
   * @return {Map<string, Category>}
   * @private
   */
  static _extractCategories(rawCatalogItems, catalogType) {
    const categories = new Map();
    rawCatalogItems.forEach(item => {
      const name = Parser._parseCategory(item);
      const order = Parser._parseCategoryOrder(item);
      categories.set(name, new Category({ name, order, catalogType }));
    });

    return categories;
  }

  /**
   * UUID is the secondary identifier of an MPC product item.
   * UUID reflects item's global configuration, common across multiple offers
   * (e.g. you can think of most of the attributes as being bound to UUID instead of the ID).
   * For most usages inside this app the ID (a.k.a OID) is the right identifier to use.
   *
   * @param {Object} catalogItem
   * @return {number}
   * @private
   */
  static _parseUuid(catalogItem) {
    return catalogItem.uuid;
  }

  /**
   * Name represents the commercial name as displayed to the user.
   * InProgress `commercialName` and `characteristicValue` `Optional Name` overrides the catalog name.
   *
   * @param {Object} catalogItem
   * @param {Object} orderItem
   * @param {Object} eligibleItem
   * @param {Object} inProgressItem
   * @return {string}
   * @private
   */
  static _parseName(catalogItem, orderItem, eligibleItem, inProgressItem) {
    if (eligibleItem && eligibleItem.characteristicValue) {
      const eligibleItemName = Parser._parseCharacteristicValue(
        eligibleItem,
        CHARACTERISTIC_VALUE_OPTIONAL_NAME,
      );
      if (eligibleItemName) {
        return eligibleItemName;
      }
    }

    if (orderItem && orderItem.characteristicValue) {
      const orderItemName = Parser._parseCharacteristicValue(
        orderItem,
        CHARACTERISTIC_VALUE_OPTIONAL_NAME,
      );
      if (orderItemName) {
        return orderItemName;
      }
    }

    if (inProgressItem && inProgressItem.commercialName) {
      return inProgressItem.commercialName;
    }

    return catalogItem.name;
  }

  /**
   * Creates slug from catalog name.
   * Do not use as identifier. Only for visual purposes.
   * Does not change with inProgress item (see _parseName)
   *
   * @see _parseName
   * @param {Object} catalogItem
   * @returns {string}
   * @private
   */
  static _parseSlug(catalogItem) {
    return removeDiacritics(catalogItem.name)
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
      .replace(/\s+/g, '-') // collapse whitespace and replace by `-`
      .replace(/-+/g, '-'); // collapse dashes
  }

  /**
   * Represents time duration for which service will be active and billed.
   *
   * @param {Object} catalogOrSummaryItem
   * @return {?Period}
   * @private
   */
  static _parsePeriod(catalogOrSummaryItem) {
    const amount = catalogOrSummaryItem.period ? parseInt(catalogOrSummaryItem.period, 10) : null;
    const unit = catalogOrSummaryItem.periodUnit ?? null;

    if (amount === null || unit === null) {
      return null;
    }

    return new Period({ amount, unit });
  }

  /**
   * Flag that says the price should not be displayed. Mostly used for "enabler" services, which are technically free,
   * but may result in additional charges.
   *
   * @param {Object} catalogItem
   * @return {boolean}
   * @private
   */
  static _parseIsPriceHidden(catalogItem) {
    return catalogItem.hidePrice !== undefined;
  }

  /**
   * Description of the service.
   *
   * @param {Object} catalogItem
   * @return {string}
   * @private
   */
  static _parseDescription(catalogItem) {
    return catalogItem.descr;
  }

  /**
   * Name of a service category, is also currently used as it's identifier.
   *
   * @param {Object} catalogItem
   * @return {string}
   * @private
   */
  static _parseCategory(catalogItem) {
    return catalogItem.category;
  }

  /**
   * Sequential order in which the categories are displayed to the user.
   *
   * @param {Object} catalogItem
   * @return {number}
   * @private
   */
  static _parseCategoryOrder(catalogItem) {
    return parseInt(catalogItem.gNum, 10); // TODO remove parseInt, OST sending as string
  }

  /**
   * Represents "L2.L3" string from MPC category structure,
   * used for mHub category identification.
   *
   * L2 and L3 categories are not mandatory:
   * - missing L3 => only L2 is returned
   * - missing L2 and L3 => default string "Others" is returned
   *
   * @param {Object} catalogItem
   * @return {string}
   * @private
   */
  static _parseMHubCategoryId(catalogItem) {
    if (!catalogItem.mHub) {
      return 'Others';
    }

    const split = catalogItem.mHub.split('.');
    if (split.length <= 1) {
      return 'Others';
    }
    if (split.length === 2) {
      return split[1];
    }
    return `${split[1]}.${split[2]}`;
  }

  /**
   * Whether or not the service is a "subsidy" service.
   * Service is said to be "subsidy", if it's price counts towards the
   * subsidy discount for a device.
   * Purchase of a subsidy service is binding, and it can't be removed only replaced by
   * other subsidy services with the same or higher value.
   *
   * @param {Object} catalogItem
   * @return {boolean}
   * @private
   */
  static _parseIsSubsidy(catalogItem) {
    return catalogItem.isSubsidy !== undefined;
  }

  /**
   * Whether or not the service is recommended.
   * Recommended services should be displayed preferentially.
   *
   * @param {Object} catalogItem
   * @return {boolean}
   * @private
   */
  static _parseIsRecommended(catalogItem) {
    return catalogItem.isRecommended !== undefined;
  }

  /**
   * Whether or not the service is eligible for purchase.
   *
   * @param {?Object} eligibleItem
   * @param {?Object} inProgressItem
   * @return {boolean}
   * @private
   */
  static _parseIsEligible(eligibleItem, inProgressItem) {
    return (
      eligibleItem !== undefined &&
      !Parser._parseIsActivating(inProgressItem) &&
      !Parser._parseIsDeactivating(inProgressItem)
    );
  }

  /**
   * Whether or not the service is currently active - the user purchased it.
   * Might also mean the service is currently being activated or deactivated.
   *
   * @param {?Object} orderItem
   * @param {?Object} inProgressItem
   * @return {boolean}
   * @private
   */
  static _parseIsActive(orderItem, inProgressItem) {
    return (
      orderItem !== undefined ||
      Parser._parseIsActivating(inProgressItem) ||
      Parser._parseIsDeactivating(inProgressItem)
    );
  }

  /**
   * Whether or not the service can be deactivated.
   *
   * @param {Object} catalogItem
   * @return {boolean}
   * @private
   */
  static _parseCanBeDeactivated(catalogItem) {
    return catalogItem.isDoNotDeactivate === undefined;
  }

  /**
   * Whether or not is the service in the process of being activated.
   *
   * @param {?Object} inProgressItem
   * @return {boolean}
   * @private
   */
  static _parseIsActivating(inProgressItem) {
    return ['ACTIVATING', 'Pending active'].includes(inProgressItem?.status);
  }

  /**
   * Whether or not is the service in the process of being deactivated.
   *
   * @param {?Object} inProgressItem
   * @return {boolean}
   * @private
   */
  static _parseIsDeactivating(inProgressItem) {
    return ['DEACTIVATING', 'Pending terminate'].includes(inProgressItem?.status);
  }

  /**
   * Whether or not was the service initially in change state.
   * This represents processing state with attribute values change.
   *
   * Only way to detect this state is check, if service has some
   * previous attribute values.
   *
   * If is this used in combination with `isActivating`/`isDeactivating`,
   * check `isInitiallyChanging` first. Service in change state is in both states,
   * activating and changing in same time.
   *
   * @param {?Object} orderItem
   * @param {?Object} oldAttributeValuesItem
   * @param {?Object} inProgressItem
   * @return {boolean}
   * @private
   */
  static _parseIsInitiallyChanging(orderItem, oldAttributeValuesItem, inProgressItem) {
    const hasPreviousAttributeValueItems =
      (orderItem && orderItem.paramServiceSpecificationData) ||
      (oldAttributeValuesItem && oldAttributeValuesItem.paramServiceSpecificationData);

    return !!(
      Parser._parseIsActive(orderItem, inProgressItem) &&
      Parser._parseIsActivating(inProgressItem) &&
      hasPreviousAttributeValueItems
    );
  }

  /**
   * Identifier of service in OST basket.
   * Used for deactivation/change of service in basket.
   * This ID is only available for active services.
   *
   * @param {?Object} orderItem
   * @return {number|null}
   * @private
   */
  static _parseCustomerOrderId(orderItem) {
    if (!orderItem || !orderItem.customerOrderItemId) {
      return null;
    }
    return orderItem && orderItem.customerOrderItemId;
  }

  /**
   * Price of the service, returns an object that contains one or both
   * types of prices.
   * Service can have so called "cycle" or monthly price, and "purchase" or
   * one-time price (e.g. activation fee). A service might have both, but it's rare.
   * If `orderItem` and `eligibleItem` does not contain price, parse price from `catalogItem`.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} inProgressItem
   * @return {Price}
   * @private
   */
  static _parsePrice(catalogItem, orderItem, eligibleItem, inProgressItem) {
    let tariffSpecification;
    if (orderItem?.tariffSpecification) {
      tariffSpecification = orderItem.tariffSpecification;
    }
    if (eligibleItem?.tariffSpecification) {
      tariffSpecification = eligibleItem.tariffSpecification;
    }
    if (inProgressItem?.priceData?.length) {
      tariffSpecification = inProgressItem.priceData;
    }

    if (!tariffSpecification) {
      tariffSpecification = catalogItem.priceData;
    }

    return Parser._parsePriceObjectFromTariffSpecification(tariffSpecification);
  }

  /**
   * Original price of the service (before applied discounts), returns an object that contains one or both
   * types of prices.
   * Service can have so called "cycle" or monthly price, and "purchase" or
   * one-time price (e.g. activation fee). A service might have both, but it's rare.
   * Discount should be displayed only if originalPrice exists.
   * For services without original price is returned Price object with both null values.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @return {Price}
   * @private
   */
  static _parseOriginalPrice(orderItem, eligibleItem) {
    let tariffSpecification;
    if (orderItem?.tariffSpecification) {
      tariffSpecification = orderItem.tariffSpecification;
    }
    if (eligibleItem?.tariffSpecification) {
      tariffSpecification = eligibleItem.tariffSpecification;
    }

    if (!tariffSpecification) {
      return new Price(null, null);
    }

    return Parser._parsePriceObjectFromTariffSpecification(tariffSpecification, 'originalPrice');
  }

  /**
   * Unit price of the service, returns an object that contains one or both
   * types of prices.
   * Service can have so called "cycle" or monthly price, and "purchase" or
   * one-time price (e.g. activation fee). A service might have both, but it's rare.
   * Not every service has unit price, for now it is used only for parametric
   * services with multiplication parameter, e.g. `Amount`.
   * For services without unit price is returned Price object with both null values.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @return {Price}
   * @private
   */
  static _parseUnitPrice(orderItem, eligibleItem) {
    const unitTariffSpecification = [];
    if (orderItem?.unitTariffSpecification) {
      unitTariffSpecification.push(orderItem.unitTariffSpecification);
    }
    if (eligibleItem?.unitTariffSpecification) {
      unitTariffSpecification.push(eligibleItem.unitTariffSpecification);
    }

    if (unitTariffSpecification.length === 0) {
      return new Price(null, null);
    }

    return Parser._parsePriceObjectFromTariffSpecification(unitTariffSpecification);
  }

  /**
   * Pull the price from the summary response object. Summary has a very different structure
   * for representing prices, but resulting object will be the same.
   *
   * @see _parsePrice
   * @param {Object} summaryItem
   * @return {Price}
   * @private
   */
  static _parseSummaryPrice(summaryItem) {
    const cyclePrice =
      summaryItem.cyclePrice !== undefined ? parseFloat(summaryItem.cyclePrice) : null;
    const purchasePrice =
      summaryItem.purchasePrice !== undefined ? parseFloat(summaryItem.purchasePrice) : null;

    return new Price(cyclePrice, purchasePrice);
  }

  /**
   * Pull the price from "tariffSpecification" object. Price objects are identified by the recurrence
   * type. Only "CYCLE" and "PURCHASE" types are known, but the documentation isn't exhaustive.
   *
   * @param {Object[]} tariffSpecification
   * @param {string} recurrenceType
   * @param {string} priceKey - object key from which price will be extracted
   * @return {number|null}
   * @private
   */
  static _parseSinglePriceTypeFromTariffSpecification(
    tariffSpecification,
    recurrenceType,
    priceKey = 'price',
  ) {
    const price = tariffSpecification
      .filter(item => item[priceKey]?.amount)
      .filter(item => item.recurrenceType === recurrenceType)
      .map(item => item[priceKey]?.amount)
      .pop();

    return price !== undefined ? parseFloat(price) : null; // TODO: remove parseFloat after OST fix
  }

  /**
   * Pull the cycle and purchase prices from "tariffSpecification" object and create `Price` object.
   *
   * @see _parseSinglePriceTypeFromTariffSpecification
   * @param {Object[]} tariffSpecification
   * @param {string} priceKey - object key from which price will be extracted
   * @return {Price}
   * @private
   */
  static _parsePriceObjectFromTariffSpecification(tariffSpecification, priceKey = 'price') {
    const cyclePrice = Parser._parseSinglePriceTypeFromTariffSpecification(
      tariffSpecification,
      'CYCLE',
      priceKey,
    );
    const purchasePrice = Parser._parseSinglePriceTypeFromTariffSpecification(
      tariffSpecification,
      'PURCHASE',
      priceKey,
    );

    return new Price(cyclePrice, purchasePrice);
  }

  /**
   * Date, when service was activated.
   *
   * @param {?Object} orderItem
   * @return {?string}
   * @private
   */
  static _parseActivationDate(orderItem) {
    return orderItem?.switchOnDate ?? null;
  }

  /**
   * Date, when service will change its state, e.g. from `isActivating` to `isActive`
   *
   * @param {?Object} inProgressItem
   * @return {?string}
   * @private
   */
  static _parseChangeDate(inProgressItem) {
    return inProgressItem?.changeDate ?? null;
  }

  /**
   * Date, when service will be renewed for next period.
   * Currently used for prepaid services with auto renewal.
   *
   * @param {?Object} orderItem
   * @return {?string}
   * @private
   */
  static _parseRenewalDate(orderItem) {
    return orderItem?.characteristicValue
      ? Parser._parseCharacteristicValue(orderItem, 'Renewal Date')
      : null;
  }

  /**
   * Sequential order in which the services are displayed to the user.
   *
   * @param {Object} catalogItem
   * @return {number}
   * @private
   */
  static _parseOrder(catalogItem) {
    return catalogItem.iNum;
  }

  /**
   * Whether or not the service requires additional verification.
   * Verification can be done using personal ID number (rodné číslo) or a password (ID KOD).
   * Verification is done by SSO.
   *
   * @param {Object} catalogItem
   * @return {boolean}
   * @private
   */
  static _parseIsIdRcVerificationRequired(catalogItem) {
    return catalogItem.isVerifReq !== undefined;
  }

  /**
   * Whether or not the service can be activated, deactivated or edited when customer is in process of changing tariffs.
   *
   * @param {Object} catalogItem
   * @return {boolean}
   * @private
   */
  static _parseCanServiceBeModifiedDuringChange(catalogItem) {
    return catalogItem.isDoOfferInChange !== undefined;
  }

  /**
   * Whether or not the service is in unprocessed state in database queue.
   * Services:
   * - with `inProgress` info
   * - deactivating and already deactivated (eligible)
   * - activating and already active
   * are considered not unprocessed.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} unprocessedRequestItem
   * @param {?Object} inProgressItem
   * @return {boolean}
   * @private
   */
  static _parseHasUnprocessedRequest(
    orderItem,
    eligibleItem,
    unprocessedRequestItem,
    inProgressItem,
  ) {
    const isActive = Parser._parseIsActive(orderItem, inProgressItem);
    const isEligible = Parser._parseIsEligible(eligibleItem, inProgressItem);

    if (unprocessedRequestItem === undefined || inProgressItem !== undefined) {
      return false;
    }

    return (
      (unprocessedRequestItem?.action === 'ACTIVATE' && !isActive) ||
      (unprocessedRequestItem?.action === 'DEACTIVATE' && !isEligible)
    );
  }

  /**
   * Parses info about TryAndBuy for service.
   * Returns `null` if service is not eligible for TryAndBuy.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @returns {?TryAndBuy}
   * @private
   */
  static _parseTryAndBuy(catalogItem, orderItem, eligibleItem) {
    if (catalogItem.tabStatus !== TRY_AND_BUY_STATUS_INIT) {
      return null;
    }

    const initItem = orderItem || eligibleItem;

    const status =
      Parser._parseCharacteristicValue(initItem, TRY_AND_BUY_STATUS_ATTRIBUTE_ID) ||
      catalogItem.tabStatus;
    const endDate = Parser._parseCharacteristicValue(initItem, TRY_AND_BUY_END_DATE_ATTRIBUTE_ID);
    const period = Number.parseInt(catalogItem.tabPeriod, 10);
    const periodUnit = catalogItem.tabPeriodUnit;

    if (periodUnit !== TRY_AND_BUY_PERIOD_UNIT_DAYS) {
      throw new Error(`Unsupported TryAndBuy period "${periodUnit}"`);
    }

    return new TryAndBuy({ status, endDate, period, periodUnit });
  }

  /**
   * Parses UUID of related service.
   * Related service is currently used for prepaid SN, where it indicates relationship
   * between service with and without automatic recurrence.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @returns {?number}
   * @private
   */
  static _parseRelatedServiceUuid(orderItem, eligibleItem) {
    let relatedServiceUuid = null;
    if (orderItem?.characteristicValue) {
      relatedServiceUuid = Parser._parseCharacteristicValue(
        orderItem,
        CHARACTERISTIC_VALUE_RELATED_SERVICE,
      );
    }
    if (eligibleItem?.characteristicValue) {
      relatedServiceUuid = Parser._parseCharacteristicValue(
        eligibleItem,
        CHARACTERISTIC_VALUE_RELATED_SERVICE,
      );
    }

    return relatedServiceUuid ? parseInt(relatedServiceUuid, 10) : null;
  }

  /**
   * Parses type of the related service.
   * Indicates type of the relationship that related service has respectively to the current service.
   *
   * We support 4 relationship types altho we currently visualize only the first 3:
   *  - cycle -> related service is version of the current service with automatic renewal
   *  - one-time -> related service is one-shot version of renewable service
   *  - one-time-option -> related service is eligible for an add-on in the form of the current service
   *  - extension -> related service is an add-on of sorts of the current service
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @returns {?string}
   * @private
   */
  static _parseRelatedServiceType(orderItem, eligibleItem) {
    let relatedServiceType = null;
    if (orderItem?.characteristicValue) {
      relatedServiceType = Parser._parseCharacteristicValue(
        orderItem,
        CHARACTERISTIC_VALUE_RELATED_SERVICE_TYPE,
      );
    }
    if (eligibleItem?.characteristicValue) {
      relatedServiceType = Parser._parseCharacteristicValue(
        eligibleItem,
        CHARACTERISTIC_VALUE_RELATED_SERVICE_TYPE,
      );
    }

    if (relatedServiceType && !RELATED_SERVICE_TYPES.includes(relatedServiceType)) {
      throw new Error(`Unsupported related service type "${relatedServiceType}"`);
    }

    return relatedServiceType;
  }

  /**
   * Processes known/used attributes - additional settings or values for services.
   * This part of responses is very messy.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @return {Attribute[]}
   * @private
   * @static
   */
  static _parseAttributes(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const attributes = [];

    catalogItem.attributes?.forEach(attr => {
      switch (attr.id) {
        case AMOUNT_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseAmountAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case SN_LIST_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseSnListAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case NEW_SN_LIST_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseNewSnListAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case GOOGLE_ACCOUNT_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseGoogleAccountAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case CONTACT_SN_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseContactSnAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case EMAIL_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEmailAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case REPORT_EMAIL_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceReportEmail(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case ENCRYPTION_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceEncryptReport(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case PASSWORD_ATTRIBUTE_ID:
          attributes.push(Parser._parseEInvoicePassword());
          break;
        case PASSWORD_CONFIRM_ATTRIBUTE_ID:
          attributes.push(Parser._parseEInvoicePasswordConfirm());
          break;
        case NOTIFICATION_SN_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceNotificationSn(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case NOTIFICATION_EMAIL_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceNotificationEmail(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case NOTIFY_MASTER_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceNotifyMaster(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          attributes.push(
            Parser._parseEInvoiceNotifyWithPrice(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case NOTIFY_SLAVES_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceNotifySlaves(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case DAY_OF_WEEK_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseEInvoiceNotificationTime(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case TIME_OF_DAY_ATTRIBUTE_ID:
          // Intentionally left blank. Combined with DAY_OF_WEEK.
          break;
        case STREET_NAME_ATTRIBUTE_ID:
          attributes.push(Parser._parseAddress(orderItem, eligibleItem, oldAttributeValuesItem));
          break;
        case HOUSE_NUMBER_ATTRIBUTE_ID:
          // Intentionally left blank. Combined with STREET_NAME into AddressAttribute.
          break;
        case CITY_ATTRIBUTE_ID:
          // Intentionally left blank. Combined with STREET_NAME into AddressAttribute.
          break;
        case ZIP_CODE_ATTRIBUTE_ID:
          // Intentionally left blank. Combined with STREET_NAME into AddressAttribute.
          break;
        case DEVICE_NAME_ATTRIBUTE_ID:
          attributes.push(Parser._parseDevice(orderItem, eligibleItem, oldAttributeValuesItem));
          break;
        case IMEI_ATTRIBUTE_ID:
          // Intentionally left blank. Combined with DEVICE_NAME.
          break;
        case LICENCE_COUNT_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseLicenceCountAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case TOGETHER_FREE_GROUP_COUNT_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseTogetherFreeGroupCountAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case END_DATE_LIST_ATTRIBUTE_ID:
          attributes.push(Parser._parseEndDateListAttribute(orderItem, eligibleItem));
          break;
        case DATA_CAP_ATTRIBUTE_ID:
          attributes.push(Parser._parseDataCapAttribute(catalogItem, orderItem, eligibleItem));
          break;
        case CONSUMED_AMOUNT_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseConsumedAmountAttribute(catalogItem, orderItem, eligibleItem),
          );
          break;
        case SN_ATTRIBUTE_ID:
          attributes.push(Parser._parseSnAttribute(catalogItem, orderItem, eligibleItem));
          break;
        case CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseCyberFilterContactSnAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseCyberFilterLicenseListAttribute(catalogItem, orderItem, eligibleItem),
          );
          break;
        case PRIMA_KIDS_PREPAID_SN_LIST_ATTRIBUTE_ID:
          attributes.push(Parser._parsePrimaKidsPrepaidSnListAttribute(orderItem, eligibleItem));
          break;
        case PRIMA_KIDS_PREPAID_SN_LIST_WAITING_ATTRIBUTE_ID:
          // handled in different attribute parsing
          // @see _parsePrimaKidsPrepaidSnListAttribute
          break;
        case PRIMA_KIDS_REMAINING_MONTHLY_LIMIT_ATTRIBUTE_ID:
          attributes.push(Parser._parsePrimaKidsMonthlyLimitAttribute(orderItem, eligibleItem));
          break;
        case VOLUME_ATTRIBUTE_ID:
          attributes.push(
            Parser._parseVolumeAttribute(
              catalogItem,
              orderItem,
              eligibleItem,
              oldAttributeValuesItem,
            ),
          );
          break;
        case TRY_AND_BUY_END_DATE_ATTRIBUTE_ID:
        case TRY_AND_BUY_STATUS_ATTRIBUTE_ID:
          // handled in separate parser method
          // @see _parseTryAndBuy
          break;
        default:
          break;
      }
    });

    if (catalogItem.channels) {
      attributes.push(Parser._parseChannelsAttribute(catalogItem));
    }

    return attributes;
  }

  /**
   *
   * @param {Object} catalogItem
   * @param {string} attributeId
   * @return {boolean}
   */
  static _parseIsReadOnlyCatalogAttribute(catalogItem, attributeId) {
    const attribute = catalogItem.attributes.find(attr => attr.id === attributeId);
    return attribute && attribute.readOnly && attribute.readOnly === true;
  }

  /**
   * This attribute is used for services that allow user to purchase a certain quantity of something
   * (e.g. prepaid data or messages). Amount selection is never linear, it's always composed
   * of discrete options with specific prices. Price might not increase linearly with the amount.
   *
   * Options are parsed from both catalog and init response items (order and eligible).
   * Preferred options are from init response items.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @return {AmountAttribute}
   * @private
   */
  static _parseAmountAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    // parse AmountOptions from order or eligible item (init response)
    const amountOptionsAttributeValue = this._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      AMOUNT_ATTRIBUTE_OPTION_ID,
      null,
    );

    let initOptions = [];
    if (amountOptionsAttributeValue) {
      initOptions = amountOptionsAttributeValue
        .split(',')
        .map(option => option.split('|'))
        .map(
          option =>
            new AmountAttributeOption({
              value: parseInt(option[0], 10),
              label: option[1],
              price: parseFloat(option[2]),
            }),
        );
    }

    // parse AmountOptions from catalog item
    const catalogOptions =
      catalogItem.quantityAmounts?.map(
        item =>
          new AmountAttributeOption({
            label: item.name,
            value: parseInt(item.value, 10), // TODO: remove parseInt after OST fix
            price: item.price ? parseFloat(item.price) : 0, // TODO: remove parseFloat after OST fix; check why the price can be undefined
          }),
      ) ?? [];

    if (catalogOptions.length === 0 && initOptions.length === 0) {
      throw new Error(`"options" for "AmountAttribute" in "${catalogItem.name}" are empty!`);
    }

    const options = initOptions.length > 0 ? initOptions : catalogOptions;

    const value = parseInt(
      Parser._parseAttributeValue(
        orderItem,
        oldAttributeValuesItem,
        eligibleItem,
        AMOUNT_ATTRIBUTE_ID,
        options[0].value,
      ),
      10,
    );

    const parsedPreviousValue = Parser._parsePreviousAttributeValue(orderItem, AMOUNT_ATTRIBUTE_ID);
    const previousValue = parsedPreviousValue ? parseInt(parsedPreviousValue, 10) : null;

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(catalogItem, AMOUNT_ATTRIBUTE_ID);

    return new AmountAttribute({ value, options, previousValue, isReadOnly });
  }

  /**
   * Attribute for services that allow user to specify a list of phone numbers (SN).
   * Length of the list is also specified by an attribute.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @return {SnListAttribute}
   * @private
   */
  static _parseSnListAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const maxValues = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      LIST_LENGTH_ATTRIBUTE_PROPERTY_ID,
      catalogItem.maxSnItems,
    );
    if (maxValues === undefined) {
      throw new Error(
        `Cannot determine "maxValues" for "SnListAttribute" in "${catalogItem.name}"`,
      );
    }

    const valueString = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      SN_LIST_ATTRIBUTE_ID,
      '',
    );
    const previousValueString = Parser._parsePreviousAttributeValue(
      orderItem,
      SN_LIST_ATTRIBUTE_ID,
    );

    const values = valueString.split(';').filter(sn => sn !== '');
    const previousValues = previousValueString
      ? previousValueString.split(';').filter(sn => sn !== '')
      : null;

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(catalogItem, SN_LIST_ATTRIBUTE_ID);

    return new SnListAttribute({
      maxValues: parseInt(maxValues, 10), // TODO remove parseInt, OST sending `List Length` as string
      values,
      previousValues,
      isReadOnly,
    });
  }

  /**
   * Attribute for services that allow user to specify a list of phone numbers (SN).
   * Length of the list is also specified by an attribute.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @return {NewSnListAttribute}
   * @private
   */
  static _parseNewSnListAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const maxValues = catalogItem.maxLength;
    if (maxValues === undefined) {
      throw new Error(
        `Cannot determine "maxValues" for "SnListAttribute" in "${catalogItem.name}"`,
      );
    }

    const valueString = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      NEW_SN_LIST_ATTRIBUTE_ID,
      '',
    );
    const previousValueString = Parser._parsePreviousAttributeValue(
      orderItem,
      NEW_SN_LIST_ATTRIBUTE_ID,
    );

    const values = valueString
      .split(';')
      .filter(sn => sn !== '' && sn !== ',')
      .map(value => {
        const { sn, date } = /^(?<sn>[^,]*)?,?(?<date>[^,]*)?$/.exec(value).groups;
        return { sn, editableFrom: date ? new Date(date) : new Date() };
      });

    const previousValues = previousValueString
      ? previousValueString
          .split(';')
          .filter(sn => sn !== '' && sn !== ',')
          .map(value => {
            const { sn, date } = /^(?<sn>[^,]*)?,?(?<date>[^,]*)?$/.exec(value).groups;
            return { sn, editableFrom: date ? new Date(date) : new Date() };
          })
      : null;

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      NEW_SN_LIST_ATTRIBUTE_ID,
    );

    return new NewSnListAttribute({
      maxValues: parseInt(maxValues, 10), // TODO remove parseInt, OST sending `List Length` as string
      values,
      previousValues,
      isReadOnly,
    });
  }

  /**
   * Attribute for services that require user to specify their google account.
   * Currently that's the ESET service.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {GoogleAccountAttribute}
   * @private
   */
  static _parseGoogleAccountAttribute(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      GOOGLE_ACCOUNT_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      GOOGLE_ACCOUNT_ATTRIBUTE_ID,
    );
    return new GoogleAccountAttribute({ value, isReadOnly });
  }

  /**
   * Attribute for services that allow user to specify a phone number (SN)
   * that can be used for contact purchases (might be completely unrelated to user's products).
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {ContactSnAttribute}
   * @private
   */
  static _parseContactSnAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      CONTACT_SN_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      CONTACT_SN_ATTRIBUTE_ID,
    );
    return new ContactSnAttribute({ value, isReadOnly });
  }

  /**
   * Generic email attribute. Currently only known to be used in service "Rodinný bezpečnostný balík".
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EmailAttribute}
   * @private
   */
  static _parseEmailAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      EMAIL_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(catalogItem, EMAIL_ATTRIBUTE_ID);
    return new EmailAttribute({ value, isReadOnly });
  }

  /**
   * Attribute for E-invoice service. Determines the email where the invoice itself is sent.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceReportEmailAttribute}
   * @private
   */
  static _parseEInvoiceReportEmail(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      REPORT_EMAIL_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      REPORT_EMAIL_ATTRIBUTE_ID,
    );
    return new EInvoiceReportEmailAttribute({ value, isReadOnly });
  }

  /**
   * Attribute for E-invoice service. Determines whether or not to use encryption.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceEncryptReportAttribute}
   * @private
   */
  static _parseEInvoiceEncryptReport(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      ENCRYPTION_ATTRIBUTE_ID,
      false,
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      ENCRYPTION_ATTRIBUTE_ID,
    );
    return new EInvoiceEncryptReportAttribute({ value: value === 'Y', isReadOnly });
  }

  /**
   * Password attribute value is not received from OST, so there is nothing to parse.
   *
   * @return {EInvoicePasswordAttribute}
   * @private
   */
  static _parseEInvoicePassword() {
    return new EInvoicePasswordAttribute({ value: '' });
  }

  /**
   * Password confirm attribute value is not received from OST, so there is nothing to parse.
   *
   * @return {EInvoicePasswordConfirmAttribute}
   * @private
   */
  static _parseEInvoicePasswordConfirm() {
    return new EInvoicePasswordConfirmAttribute({ value: '' });
  }

  /**
   * Attribute for E-invoice service. Determines the SN where the notifications should be sent.
   * This SN also might determine the master SN under current CN. Consider communicating this fact.
   * Despite the fact that this might determine master, the number is not thoroughly validated,
   * pretty much any valid phone number will currently go through.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceNotificationSnAttribute}
   * @private
   */
  static _parseEInvoiceNotificationSn(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      NOTIFICATION_SN_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      NOTIFICATION_SN_ATTRIBUTE_ID,
    );
    return new EInvoiceNotificationSnAttribute({ value, isReadOnly });
  }

  /**
   * Attribute for E-invoice service. Determines the email where the notification about invoice is sent.
   * The invoice itself is NOT sent to this email address.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceNotificationEmailAttribute}
   * @private
   */
  static _parseEInvoiceNotificationEmail(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      NOTIFICATION_EMAIL_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      NOTIFICATION_EMAIL_ATTRIBUTE_ID,
    );
    return new EInvoiceNotificationEmailAttribute({ value, isReadOnly });
  }

  /**
   * Attribute for E-invoice service. Determines whether or not to send the SMS notification
   * to the notification SN (set in the other attribute).
   *
   * @see _parseEInvoiceNotificationSn
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceNotifyMasterAttribute}
   * @private
   */
  static _parseEInvoiceNotifyMaster(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      NOTIFY_MASTER_ATTRIBUTE_ID,
      false,
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      NOTIFY_MASTER_ATTRIBUTE_ID,
    );
    return new EInvoiceNotifyMasterAttribute({
      value: value === 'Y' || value === 'Y_NOFIN',
      isReadOnly,
    });
  }

  /**
   * Attribute for E-invoice service. Determines whether or not to send total invoice price in
   * the SMS notification sent to the master SN. This might not apply to slave SNs.
   * TODO: Find out how this interacts with slave SNs.
   *
   * @see _parseEInvoiceNotificationSn
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceNotifyWithPrice}
   * @private
   */
  static _parseEInvoiceNotifyWithPrice(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      NOTIFY_MASTER_ATTRIBUTE_ID,
      false,
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      NOTIFY_MASTER_ATTRIBUTE_ID,
    );
    return new EInvoiceNotifyWithPrice({ value: value === 'Y' || value === 'N', isReadOnly });
  }

  /**
   * Attribute for E-invoice service. Determines whether or not to send the SMS notification
   * to all other numbers under current CN.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceNotifySlavesAttribute}
   * @private
   */
  static _parseEInvoiceNotifySlaves(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      NOTIFY_SLAVES_ATTRIBUTE_ID,
      false,
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      NOTIFY_SLAVES_ATTRIBUTE_ID,
    );
    return new EInvoiceNotifySlavesAttribute({ value: value === 'Y', isReadOnly });
  }

  /**
   * Attribute for E-invoice service, that determines when the SMS notifications should be delivered.
   * In OST it's represented byt 2 attributes:
   * - "Only workdays" which determines the days of the week when they can be delivered ("WORKDAYS" or "UNLIMITED")
   * - "All day hours" which determines the time of day when they can be delivered ("DAY-TIME" or "UNLIMITED")
   *
   * We represent it as a single attribute with options LIMITED and NONSTOP. Conversion of possible combinations was
   * determined like this:
   * WORKDAYS + DAY-TIME = LIMITED;
   * WORKDAYS + UNLIMITED = LIMITED;
   * UNLIMITED + DAY-TIME = NONSTOP;
   * UNLIMITED + UNLIMITED = NONSTOP;
   * (yes, essentially time-of-day no longer matters)
   *
   * @see EInvoiceNotificationTimeAttribute
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {EInvoiceNotificationTimeAttribute}
   * @private
   */
  static _parseEInvoiceNotificationTime(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const dayOfWeek = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      DAY_OF_WEEK_ATTRIBUTE_ID,
      OPTION_NONSTOP,
    );

    const value = dayOfWeek === 'WORKDAYS' ? OPTION_LIMITED : OPTION_NONSTOP;

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      DAY_OF_WEEK_ATTRIBUTE_ID,
    );

    return new EInvoiceNotificationTimeAttribute({ value, isReadOnly });
  }

  /**
   * Attribute for services that display customers address.
   * In OST it's represented by 4 self explanatory attributes:
   * - Ulica
   * - Čǐslo
   * - Obec
   * - Psč
   * However we've chosen to represent them as a single `AddressAttribute` with parts of the address as properties.
   * For simplicity is this attribute readOnly, needs modification in case of need of changing.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {AddressAttribute}
   * @private
   */
  static _parseAddress(orderItem, eligibleItem, oldAttributeValuesItem) {
    const streetName = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      STREET_NAME_ATTRIBUTE_ID,
      '',
    );

    const houseNumber = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      HOUSE_NUMBER_ATTRIBUTE_ID,
      '',
    );

    const city = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      CITY_ATTRIBUTE_ID,
      '',
    );

    const zipCode = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      ZIP_CODE_ATTRIBUTE_ID,
      '',
    );

    return new AddressAttribute({ streetName, houseNumber, city, zipCode, isReadOnly: true });
  }

  /**
   * Attribute for services that display device info.
   * In OST it's represented by 2 self explanatory attributes:
   * - Device Name
   * - IMEI
   * However we've chosen to represent them as a single `DeviceAttribute` with parts of the device attributes as properties.
   * For simplicity is this attribute readOnly, needs modification in case of need of changing.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {DeviceAttribute}
   */
  static _parseDevice(orderItem, eligibleItem, oldAttributeValuesItem) {
    const name = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      DEVICE_NAME_ATTRIBUTE_ID,
      '',
    );

    const imeiCode = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      IMEI_ATTRIBUTE_ID,
      '',
    );

    return new DeviceAttribute({ name, imeiCode, isReadOnly: true });
  }

  /**
   * Represents `licence_count` attribute.
   * It is renamed `amount` attribute on OST side without `quantityAmounts` list.
   * Value (number of licences) is parsed as Amount attribute value.
   *
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @return {LicenceCountAttribute}
   * @see {@link _parseAmountAttribute}
   * @private
   */
  static _parseLicenceCountAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = parseInt(
      Parser._parseAttributeValue(
        orderItem,
        oldAttributeValuesItem,
        eligibleItem,
        AMOUNT_ATTRIBUTE_ID,
        0,
      ),
      10,
    );

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      LICENCE_COUNT_ATTRIBUTE_ID,
    );

    return new LicenceCountAttribute({ value, isReadOnly });
  }

  /*
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @return {TfGroupCountAttribute}
   * @private
   */
  static _parseTogetherFreeGroupCountAttribute(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const value = parseInt(
      Parser._parseAttributeValue(
        orderItem,
        oldAttributeValuesItem,
        eligibleItem,
        AMOUNT_ATTRIBUTE_ID,
        0,
      ),
      10,
    );

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      TOGETHER_FREE_GROUP_COUNT_ATTRIBUTE_ID,
    );

    return new TogetherFreeGroupCountAttribute({ value, isReadOnly });
  }

  /**
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @return {EndDateListAttribute}
   * @private
   */
  static _parseEndDateListAttribute(orderItem, eligibleItem) {
    const attributeValues = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      END_DATE_LIST_ATTRIBUTE_ID,
      '',
    );

    const values = attributeValues ? attributeValues.split(';') : [];

    return new EndDateListAttribute({ values });
  }

  /**
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @return {DataCapAttribute}
   * @private
   */
  static _parseDataCapAttribute(catalogItem, orderItem, eligibleItem) {
    const parsedValue = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      DATA_CAP_ATTRIBUTE_ID,
      null,
    );

    const value = parsedValue !== null ? parseInt(parsedValue, 10) : null;
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(catalogItem, DATA_CAP_ATTRIBUTE_ID);

    return new DataCapAttribute({ value, isReadOnly });
  }

  /**
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @return {ConsumedAmountAttribute}
   * @private
   */
  static _parseConsumedAmountAttribute(catalogItem, orderItem, eligibleItem) {
    const parsedValue = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      CONSUMED_AMOUNT_ATTRIBUTE_ID,
      null,
    );

    const value = parsedValue !== null ? parseFloat(parsedValue) : null;
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      CONSUMED_AMOUNT_ATTRIBUTE_ID,
    );

    return new ConsumedAmountAttribute({ value, isReadOnly });
  }

  /**
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @return {SnAttribute}
   * @private
   */
  static _parseSnAttribute(catalogItem, orderItem, eligibleItem) {
    const value = Parser._parseAttributeValue(orderItem, null, eligibleItem, SN_ATTRIBUTE_ID, '');
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(catalogItem, SN_ATTRIBUTE_ID);

    return new SnAttribute({ value, isReadOnly });
  }

  /**
   * Parses list of channel from `catalogItem`.
   * This is technically not attribute, but we handle channels as one.
   *
   * @param {Object} catalogItem
   * @returns {ChannelsAttribute}
   */
  static _parseChannelsAttribute(catalogItem) {
    const channels = catalogItem.channels.map(channel => ({
      name: channel.name,
      logoUrl: channel.logo,
    }));

    return new ChannelsAttribute({ values: channels });
  }

  /**
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @param {?Object} oldAttributeValuesItem
   * @return {CyberFilterContactSnAttribute}
   * @private
   */
  static _parseCyberFilterContactSnAttribute(
    catalogItem,
    orderItem,
    eligibleItem,
    oldAttributeValuesItem,
  ) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID,
      '',
    );
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID,
    );
    return new CyberFilterContactSnAttribute({ value, isReadOnly });
  }

  /**
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @return {CyberFilterLicenseListAttribute}
   * @private
   */
  static _parseCyberFilterLicenseListAttribute(catalogItem, orderItem, eligibleItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID,
      '',
    );

    const licenses = value
      .split('#')
      .filter(item => item !== '')
      .map(item => item.split(';'))
      .map(license => ({
        id: license[0],
        state: license[1],
        date: license[2],
        flag: license[3],
      }));

    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(
      catalogItem,
      CYBER_FILTER_LICENSE_LIST_ATTRIBUTE_ID,
    );

    return new CyberFilterLicenseListAttribute({ licenses, isReadOnly });
  }

  /**
   * Attribute for list of active/activating prepaid SNs in Prima Kids service.
   * In OST it's represented by 2 attributes:
   * - PrepaidSnList
   * - PrepaidSnListActivating
   * However we've chosen to represent them as a single `PrimaKidsPrepaidSnList`.
   * This attribute is readOnly by design.
   *
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @return {PrimaKidsPrepaidSnList}
   * @private
   */
  static _parsePrimaKidsPrepaidSnListAttribute(orderItem, eligibleItem) {
    const activeSnList = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      PRIMA_KIDS_PREPAID_SN_LIST_ATTRIBUTE_ID,
      '',
    )
      .split(',')
      .filter(sn => sn !== '')
      .map(sn => ({ sn, isActive: true }));

    const waitingSnList = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      PRIMA_KIDS_PREPAID_SN_LIST_WAITING_ATTRIBUTE_ID,
      '',
    )
      .split(',')
      .filter(sn => sn !== '')
      .map(sn => ({ sn, isActive: false }));

    const values = activeSnList.concat(waitingSnList);

    return new PrimaKidsPrepaidSnList({ values });
  }

  /**
   * @param {?Object} orderItem
   * @param {?Object} eligibleList
   * @return {PrimaKidsRemainingMonthlyLimit}
   * @private
   */
  static _parsePrimaKidsMonthlyLimitAttribute(orderItem, eligibleItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      null,
      eligibleItem,
      PRIMA_KIDS_REMAINING_MONTHLY_LIMIT_ATTRIBUTE_ID,
      0,
    );

    const monthlyLimit = parseFloat(value); // TODO remove parseFloat, OST sending string;

    return new PrimaKidsRemainingMonthlyLimit({ value: monthlyLimit });
  }

  /**
   * @param {Object} catalogItem
   * @param {?Object} orderItem
   * @param {?Object} eligibleItem
   * @param {?Object} oldAttributeValuesItem
   * @returns {VolumeAttribute}
   */
  static _parseVolumeAttribute(catalogItem, orderItem, eligibleItem, oldAttributeValuesItem) {
    const value = Parser._parseAttributeValue(
      orderItem,
      oldAttributeValuesItem,
      eligibleItem,
      VOLUME_ATTRIBUTE_ID,
      0,
    );

    const volume = parseInt(value, 10);
    const isReadOnly = Parser._parseIsReadOnlyCatalogAttribute(catalogItem, VOLUME_ATTRIBUTE_ID);

    return new VolumeAttribute({ value: volume, isReadOnly });
  }

  /**
   * Extract an attribute value from OST response.
   * Value is taken preferentially from the order and oldAttributeValuesItem,
   * alternatively from the eligible item.
   *
   * orderItem value - value of activated service
   * eligibleItem value - default value of eligible (inactive item)
   * oldAttributeValuesItem - value of item in deactivation state, overrides eligibleItem value
   *
   * If neither object is present or the value isn't in neither of the default value is used.
   *
   * @param {?Object} orderItem
   * @param {?Object} oldAttributeValuesItem
   * @param {?Object} eligibleItem
   * @param {string} name
   * @param {string} defaultValue
   * @return {string}
   * @private
   */
  static _parseAttributeValue(orderItem, oldAttributeValuesItem, eligibleItem, name, defaultValue) {
    if (orderItem && orderItem.characteristicValue) {
      const value = Parser._parseCharacteristicValue(orderItem, name);
      if (value !== null) {
        return value;
      }
    }
    if (oldAttributeValuesItem && oldAttributeValuesItem.paramServiceSpecificationData) {
      const value = Parser._parsePreviousAttributeValue(oldAttributeValuesItem, name);
      if (value !== null) {
        return value;
      }
    }
    if (eligibleItem && eligibleItem.characteristicValue) {
      const value = Parser._parseCharacteristicValue(eligibleItem, name);
      if (value !== null) {
        return value;
      }
    }
    return defaultValue;
  }

  /**
   * Extract a previous attribute value from OST response.
   *
   * OrderItem contains directly the attributes old values.
   * For eligible items these values are located outside of eligible section, in `oldAttributeValues`.
   *
   * @param {Object} orderOrOldAttributeValues
   * @param {string} name
   * @return {string|null}
   * @private
   */
  static _parsePreviousAttributeValue(orderOrOldAttributeValues, name) {
    if (!(orderOrOldAttributeValues && orderOrOldAttributeValues.paramServiceSpecificationData)) {
      return null;
    }

    const searchedItem = orderOrOldAttributeValues.paramServiceSpecificationData.find(
      item => item.name === name,
    );
    return searchedItem !== undefined ? searchedItem.value : null;
  }

  /**
   * Looks for characteristic value in OST response.
   *
   * @see _parseAttributeValue
   * @param {Object} orderOrEligibleItem
   * @param {string} name
   * @return {string|null}
   * @private
   */
  static _parseCharacteristicValue(orderOrEligibleItem, name) {
    const searchedItem = orderOrEligibleItem.characteristicValue.find(item => item.name === name);
    if (!searchedItem) {
      return null;
    }
    return searchedItem.value;
  }

  /**
   * Tells, if customer is in suspended state, so he can't change services.
   * `isSuspended` is information for whole basket.
   *
   * @param {Object} initResponse
   * @return {boolean}
   * @private
   */
  static _parseIsSuspended(initResponse) {
    return initResponse && initResponse.isSuspend === 'true';
  }

  /**
   * Tells, if customer has overdue invoices, so he can't change services.
   * `IsInvoiceOverdue` is information for whole basket.
   *
   * Note: this is for now used only in Prima Kids section.
   *
   * @param {?Object} initResponse
   * @return {boolean}
   * @private
   */
  static _parseHasInvoiceOverdue(initResponse) {
    return initResponse?.isInvoiceOverdue === 'true';
  }

  /**
   * Tells, if customer is in middle of changing tariff (offer).
   *
   * It indicates, that there can be some issues with managing services
   * that are not compatible with new tariff, until change in completed.
   *
   * Customer should be notified about it.
   *
   * @param {?Object} initResponse
   */
  static _parseIsChangingTariff(initResponse) {
    return initResponse?.isChangingOffer === 'true';
  }

  /**
   * Parses information about tariff, such as description.
   *
   * In case customer is changing tariff type, no tariff info returned from API.
   *
   * @param {Object} initResponse
   * @return {Tariff}
   * @private
   */
  static _parseTariff(initResponse) {
    if (!initResponse?.packageInfo || initResponse?.packageInfo.length < 1) {
      return new Tariff({});
    }

    const entries = initResponse.packageInfo;
    return new Tariff({
      uuids: entries.map(({ uuid }) => uuid),
      names: entries.map(({ name }) => name),
      descriptions: entries.map(({ description }) => description.trim()),
      price: entries.reduce((accumulator, item) => accumulator + parseFloat(item.actualPrice), 0.0),
    });
  }

  /**
   * Helps to pull basket prices from init of update response.
   *
   * @param {Object} initOrUpdateResponse
   * @return {Price|null}
   * @private
   */
  static _extractBasketPrices(initOrUpdateResponse) {
    if (!initOrUpdateResponse || !initOrUpdateResponse.shoppingCartPrices) {
      return null;
    }

    return new Price(
      initOrUpdateResponse.shoppingCartPrices.cyclePrice,
      initOrUpdateResponse.shoppingCartPrices.purchasePrice,
    );
  }

  /**
   * Helps to pull minimal monthly fee amount from init or update response.
   *
   * @param {Object} initOrUpdateResponse
   * @return {number|null}
   * @private
   */
  static _extractMinimalMonthlyFee(initOrUpdateResponse) {
    const minimalMonthlyFee = parseFloat(initOrUpdateResponse?.minimalSubsidyMf); // TODO remove parseFloat, OST sending string;
    return !Number.isNaN(minimalMonthlyFee) ? minimalMonthlyFee : null;
  }

  /**
   * Helps to pull current monthly fee amount from init or update response.
   *
   * @param {Object} initOrUpdateResponse
   * @return {number|null}
   * @private
   */
  static _extractCurrentMonthlyFee(initOrUpdateResponse) {
    const currentMonthlyFee = parseFloat(initOrUpdateResponse?.actualSubsidyMf); // TODO remove parseFloat, OST sending string;
    return !Number.isNaN(currentMonthlyFee) ? currentMonthlyFee : null;
  }

  /**
   * Helps to pull monthly fee diff amount from init or update response.
   * This can be calculated as difference between current and minimal monthly fee amount,
   * but it is safer to use value received from OST api.
   *
   * @param {Object} initOrUpdateResponse
   * @return {number|null}
   * @private
   */
  static _extractMonthlyFeeDiff(initOrUpdateResponse) {
    const monthlyFeeDiff = parseFloat(initOrUpdateResponse?.actualDiffSubsidyMf); // TODO remove parseFloat, OST sending string;
    return !Number.isNaN(monthlyFeeDiff) ? monthlyFeeDiff : null;
  }

  /**
   * Create Summary object by parsing summaryResponse.
   *
   * @param {object} summaryResponse
   * @returns {Summary}
   * @static
   */
  static createSummary(summaryResponse) {
    if (!summaryResponse) {
      throw new Error('`createSummary` requires summary response.');
    }

    if (
      summaryResponse.addedCustomerOrderItem.length +
        summaryResponse.removedCustomerOrderItem.length +
        summaryResponse.changedCustomerOrderItem.length ===
      0
    ) {
      throw new Error('Summary response contains no added/removed/changed items.');
    }

    const summary = new Summary({
      addedItems: summaryResponse.addedCustomerOrderItem.map(Parser._createSummaryItem),
      removedItems: summaryResponse.removedCustomerOrderItem.map(Parser._createSummaryItem),
      changedItems: summaryResponse.changedCustomerOrderItem.map(Parser._createSummaryItem),
    });

    PostProcessor.postProcessSummary(summary);

    return summary;
  }

  /**
   *
   * @param {object} rawSummaryItem
   * @returns {SummaryItem}
   */
  static _createSummaryItem(rawSummaryItem) {
    return new SummaryItem({
      id: parseInt(rawSummaryItem.id, 10), // TODO remove parseInt when OST will fix sending id as string
      uuid: parseInt(rawSummaryItem.uuid, 10), // TODO remove parseInt when OST will fix sending id as string
      name: rawSummaryItem.name,
      effectiveDate: rawSummaryItem.effectiveFrom ? rawSummaryItem.effectiveFrom : null,
      price: new Price(
        rawSummaryItem.cyclePrice ? parseFloat(rawSummaryItem.cyclePrice) : null, // TODO remove parseFloat when OST will fix sending price as float
        rawSummaryItem.purchasePrice ? parseFloat(rawSummaryItem.purchasePrice) : null, // TODO remove parseFloat when OST will fix sending price as float
      ),
      previousPrice: new Price(
        rawSummaryItem.cyclePrice && rawSummaryItem.changedPrice?.oldPrice
          ? parseFloat(rawSummaryItem.changedPrice.oldPrice)
          : null, // TODO remove parseFloat when OST will fix sending price as float
        null, // previous price is never of "purchase" type
      ),
      period: Parser._parsePeriod(rawSummaryItem),
      attributes: Parser._createSummaryAttributes(rawSummaryItem),
      isPriceHidden: !!rawSummaryItem.addAttributes?.hidePrice,
    });
  }

  /**
   * @param {object} rawSummaryItem
   * @return {object[]}
   */
  static _createSummaryAttributes(rawSummaryItem) {
    if (
      !rawSummaryItem ||
      !rawSummaryItem.changedAttributes ||
      rawSummaryItem.changedAttributes.length === 0
    ) {
      return [];
    }

    return rawSummaryItem.changedAttributes.map(attribute => ({
      id: attribute.name,
      oldValue: Parser._formatSummaryAttributeValue(attribute.name, attribute.oldValue),
      newValue: Parser._formatSummaryAttributeValue(attribute.name, attribute.newValue),
    }));
  }

  /**
   * Formats value of summaryResponse attribute to human readable format.
   *
   * @param {string} id of the attribute
   * @param {*} value
   * @returns {*}
   */
  static _formatSummaryAttributeValue(id, value) {
    if (!value) {
      return null;
    }

    switch (id) {
      case SN_LIST_ATTRIBUTE_ID:
      case NEW_SN_LIST_ATTRIBUTE_ID:
        return value.split(';').filter(v => v !== '');
      case AMOUNT_ATTRIBUTE_ID:
        return parseInt(value, 10); // TODO remove parseInt when OST will fix sending amount as string
      default:
        return value;
    }
  }

  /**
   * Helper for parsing `checkAttributeInputs` response.
   * Returns only invalid attributes and ignores valid attributes.
   *
   * @param {object} checkAttributeInputsResponse
   * @returns {{isValid: boolean, attributes: Map<string, {errorMessage: string}>}}
   */
  static createValidationResponse(checkAttributeInputsResponse) {
    if (
      !checkAttributeInputsResponse?.messageDataResponse?.attributeCheckResult?.checkedParameter
    ) {
      throw new Error('checkAttributeInputs response does not contain any items');
    }

    const { attributeCheckResult } = checkAttributeInputsResponse.messageDataResponse;

    const response = {
      isValid: attributeCheckResult.returnValue === '1',
      attributes: new Map(),
    };

    const checkedParameters = Array.isArray(attributeCheckResult.checkedParameter)
      ? attributeCheckResult.checkedParameter
      : [attributeCheckResult.checkedParameter];

    checkedParameters.forEach(attribute => {
      if (attribute.returnValue === '1') {
        return;
      }

      const types = Parser._getAttributeTypeByOstAttributeId(attribute.attribute.value);
      if (!types) {
        throw new Error(`Unhandled attribute type ${attribute.attribute.value}`);
      }

      types.forEach(type => {
        response.attributes.set(type, {
          errorMessage: attribute.errorMessage ? attribute.errorMessage : null,
        });
      });
    });

    return response;
  }

  /**
   * Maps `ostAttributeId` to ecare `attributeType`.
   *
   * @param {string} ostAttributeId
   * @returns {?(string[])}
   */
  static _getAttributeTypeByOstAttributeId(ostAttributeId) {
    const map = {
      [AMOUNT_ATTRIBUTE_ID]: [AMOUNT_ATTRIBUTE_TYPE],
      [SN_LIST_ATTRIBUTE_ID]: [SN_LIST_ATTRIBUTE_TYPE],
      [NEW_SN_LIST_ATTRIBUTE_ID]: [NEW_SN_LIST_ATTRIBUTE_TYPE],
      [EMAIL_ATTRIBUTE_ID]: [EMAIL_ATTRIBUTE_TYPE],
      [GOOGLE_ACCOUNT_ATTRIBUTE_ID]: [GOOGLE_ACCOUNT_ATTRIBUTE_TYPE],
      [CONTACT_SN_ATTRIBUTE_ID]: [CONTACT_SN_ATTRIBUTE_TYPE],
      [REPORT_EMAIL_ATTRIBUTE_ID]: [EINVOICE_REPORT_EMAIL_ATTRIBUTE_TYPE],
      [ENCRYPTION_ATTRIBUTE_ID]: [EINVOICE_ENCRYPT_REPORT_ATTRIBUTE_TYPE],
      [PASSWORD_ATTRIBUTE_ID]: [EINVOICE_PASSWORD_ATTRIBUTE_TYPE],
      [PASSWORD_CONFIRM_ATTRIBUTE_ID]: [EINVOICE_PASSWORD_ATTRIBUTE_TYPE],
      [NOTIFICATION_SN_ATTRIBUTE_ID]: [EINVOICE_NOTIFICATION_SN_ATTRIBUTE_TYPE],
      [NOTIFICATION_EMAIL_ATTRIBUTE_ID]: [EINVOICE_NOTIFICATION_EMAIL_ATTRIBUTE_TYPE],
      // NOTIFY_MASTER represents two inputs on ecare side.
      [NOTIFY_MASTER_ATTRIBUTE_ID]: [
        EINVOICE_NOTIFY_MASTER_ATTRIBUTE_TYPE,
        EINVOICE_NOTIFY_WITH_PRICE_TYPE,
      ],
      [NOTIFY_SLAVES_ATTRIBUTE_ID]: [EINVOICE_NOTIFY_SLAVES_ATTRIBUTE_TYPE],
      // DAY_OF_WEEK and TIME_OF_DAY are combined in one on ecare side, see `parser.js`.
      [DAY_OF_WEEK_ATTRIBUTE_ID]: [EINVOICE_NOTIFICATION_TIME_ATTRIBUTE_TYPE],
      [TIME_OF_DAY_ATTRIBUTE_ID]: [EINVOICE_NOTIFICATION_TIME_ATTRIBUTE_TYPE],
      [SN_ATTRIBUTE_ID]: [SN_ATTRIBUTE_TYPE],
      [DATA_CAP_ATTRIBUTE_ID]: [DATA_CAP_ATTRIBUTE_TYPE],
      [CONSUMED_AMOUNT_ATTRIBUTE_ID]: [CONSUMED_AMOUNT_ATTRIBUTE_TYPE],
      [CYBER_FILTER_CONTACT_SN_ATTRIBUTE_ID]: [CYBER_FILTER_CONTACT_SN_ATTRIBUTE_TYPE],
      [PRIMA_KIDS_REMAINING_MONTHLY_LIMIT_ATTRIBUTE_ID]: [
        PRIMA_KIDS_REMAINING_MONTHLY_LIMIT_ATTRIBUTE_TYPE,
      ],
      [PRIMA_KIDS_PREPAID_SN_LIST_ATTRIBUTE_ID]: [PRIMA_KIDS_PREPAID_SN_LIST_ATTRIBUTE_TYPE],
      [VOLUME_ATTRIBUTE_ID]: [VOLUME_ATTRIBUTE_TYPE],
    };

    return map[ostAttributeId] || null;
  }
}
